import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';

/**
 * Imperative controller a campaign wizard step registers with the surrounding
 * {@link CampaignWizardLayout}. The layout's shared (sticky) header buttons invoke
 * these callbacks; the step owns the handlers because they live deep inside the
 * step's hooks (e.g. `useBuildDebtCampaignLogic`, `useAgenticReviewLogic`).
 *
 * Deliberately imperative-only — no `canProceed`/`title`. Those are non-reactive
 * through a ref and are sourced by the layout instead (see plan). Loading is the
 * one exception: it must reactively disable the layout-owned CTAs, so a step
 * reports it via the second arg of {@link useRegisterWizardStep}.
 */
export interface WizardStepController {
  /** Forward gate run before advancing; resolve `false` to block the next step. */
  validate?: () => boolean | Promise<boolean>;
  /** Ghost ("Save and Exit") button handler. */
  saveAndExit: () => void;
  /** Last-step ("Review and start") handler; opens the launch dialog. */
  complete?: () => void | Promise<void>;
}

type RegisterFn = (controller: WizardStepController) => () => void;
type SetStepLoadingFn = (isLoading: boolean) => void;

interface WizardStepControllerContextValue {
  register: RegisterFn;
  /**
   * Reports the active step's loading state up to the layout so it can disable the
   * (layout-owned) sticky header CTAs while a step's essential data loads. The
   * buttons live outside the step's keyed body, so the step's own skeleton
   * early-return cannot gate them — this channel restores the old per-route flow's
   * "buttons inert while loading" protection (and makes it a visible disable).
   */
  setStepLoading: SetStepLoadingFn;
}

const WizardStepControllerContext = createContext<WizardStepControllerContextValue | null>(null);

/**
 * Owned by the layout. Holds the active step's controller in a ref (read lazily at
 * click time, never during render) and hands steps a `register` function whose
 * unregister is identity-checked so a sibling's cleanup can't null out the
 * freshly-registered controller. Also owns the reactive `isStepLoading` flag the
 * layout feeds to the wizard.
 */
export const useWizardStepControllerRegistry = () => {
  const controllerRef = useRef<WizardStepController | null>(null);
  const [isStepLoading, setStepLoading] = useState(false);

  const register = useCallback<RegisterFn>((controller) => {
    controllerRef.current = controller;
    return () => {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    };
  }, []);

  return { controllerRef, register, isStepLoading, setStepLoading };
};

interface WizardStepControllerProviderProps {
  register: RegisterFn;
  setStepLoading: SetStepLoadingFn;
  children: ReactNode;
}

export const WizardStepControllerProvider = ({
  register,
  setStepLoading,
  children,
}: WizardStepControllerProviderProps) => {
  const value = useMemo(() => ({ register, setStepLoading }), [register, setStepLoading]);

  return (
    <WizardStepControllerContext.Provider value={value}>
      {children}
    </WizardStepControllerContext.Provider>
  );
};

/**
 * Called once per mounted step. Registers a stable wrapper that always reads the
 * latest `controller` (so fresh closures are picked up without re-running the
 * effect), unregisters on unmount, and mirrors the step's `isLoading` up to the
 * layout so the shared header CTAs are disabled while the step's data loads.
 */
export const useRegisterWizardStep = (
  controller: WizardStepController,
  isLoading = false
): void => {
  const ctx = useContext(WizardStepControllerContext);
  if (!ctx) {
    throw new Error('useRegisterWizardStep must be used within WizardStepControllerProvider');
  }
  const { register, setStepLoading } = ctx;

  // Mirror the latest controller into a ref every render so the stable wrapper
  // below dereferences current handlers at call time (the effect runs only once).
  const controllerRef = useRef(controller);
  controllerRef.current = controller;

  // Register BEFORE paint (layout effect, like the loading channel below) so the
  // layout-owned sticky-header CTAs are never live over an unregistered step: a
  // passive effect would leave a paint→effect window where clicking Continue sees
  // a null controller and `validateStep` resolves true, skipping the step's
  // validate/save and advancing anyway.
  useLayoutEffect(() => {
    return register({
      validate: () => controllerRef.current.validate?.(),
      saveAndExit: () => controllerRef.current.saveAndExit(),
      complete: () => controllerRef.current.complete?.(),
    });
  }, [register]);

  // Push loading to the layout BEFORE paint so the header CTAs are never briefly
  // clickable over a still-loading step body (clicking Save & Exit during a
  // messaging-step load could otherwise skip validation and clobber senders).
  // Reset to false on unmount / step change so the next step isn't stuck disabled.
  useLayoutEffect(() => {
    setStepLoading(isLoading);
    return () => setStepLoading(false);
  }, [isLoading, setStepLoading]);
};
